import React, { useState, useEffect } from 'react';
import { profileService, UserComment } from '../../services/profile.service';

interface CommentsTabProps {
  userId: string;
  isOwnProfile: boolean;
}

export function CommentsTab({ userId, isOwnProfile }: CommentsTabProps) {
  const [comments, setComments] = useState<UserComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadComments();
  }, [userId, page]);

  const loadComments = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await profileService.getUserComments(userId, page, 20);
      if (page === 1) {
        setComments(data);
      } else {
        setComments((prev) => [...prev, ...data]);
      }
      setHasMore(data.length === 20);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los comentarios');
    } finally {
      setLoading(false);
    }
  };

  if (loading && page === 1) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p className="text-gray-600 dark:text-gray-400">
          {isOwnProfile ? 'Aún no has comentado en ningún post' : 'Este usuario no tiene comentarios'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {comments.map((comment) => (
        <div
          key={comment.id}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          {/* Original Post Context */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              {comment.post.user.avatar_url ? (
                <img
                  src={comment.post.user.avatar_url}
                  alt={comment.post.user.display_name || 'Usuario'}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
                  <span className="text-xs font-bold text-white">
                    {(comment.post.user.display_name || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {comment.post.user.display_name || 'Usuario'}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                publicó:
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              {comment.post.content}
            </p>
          </div>

          {/* Comment */}
          <div className="mb-3">
            <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
              {comment.content}
            </p>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              {new Date(comment.created_at).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>
      ))}

      {hasMore && (
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={loading}
          className="w-full py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
          )}
          {loading ? 'Cargando...' : 'Cargar más'}
        </button>
      )}
    </div>
  );
}
